"use client";

import { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";

export default function ServiceOrderDetailsModal({
  modal_center,
  setmodal_center,
  tog_center,
  serviceOrder,
  onStatusUpdate,
}) {
  const [isUpdating, setIsUpdating] = useState(false);

  const updateServiceOrderStatus = async (newStatus) => {
    if (!serviceOrder || !serviceOrder.id) return;

    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await Api.put(
        `/admin/service-orders/${serviceOrder.id}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(`Service order status updated to ${newStatus}`, {
          position: "bottom-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          theme: "colored",
        });

        // Update the service order in parent component
        if (onStatusUpdate) {
          onStatusUpdate(serviceOrder.id, newStatus);
        }

        // Close modal
        tog_center();
      } else {
        toast.error(
          response.data.message || "Failed to update service order status",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error updating service order status:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "warning", text: "Pending" },
      accepted: { color: "info", text: "Accepted" },
      in_progress: { color: "primary", text: "In Progress" },
      completed: { color: "success", text: "Completed" },
      cancelled: { color: "danger", text: "Cancelled" },
      refunded: { color: "secondary", text: "Refunded" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      text: "Unknown",
    };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!serviceOrder) return null;

  return (
    <Modal
      isOpen={modal_center}
      toggle={tog_center}
      centered
      size="lg"
      className="service-order-details-modal"
    >
      <ModalHeader toggle={tog_center}>
        <div className="d-flex align-items-center">
          <i className="ri-shopping-cart-line me-2 text-primary"></i>
          Service Order Details
          <Badge color="light" className="ms-2">
            #{serviceOrder.order_number || serviceOrder.id}
          </Badge>
        </div>
      </ModalHeader>

      <ModalBody>
        <div className="service-order-details">
          {/* Service Order Header */}
          <Row className="mb-4">
            <Col md={8}>
              <div className="d-flex align-items-start">
                {(serviceOrder.service?.image_url ||
                  serviceOrder.service?.details?.image_url) && (
                  <img
                    src={
                      serviceOrder.service?.image_url ||
                      serviceOrder.service?.details?.image_url ||
                      "/placeholder.svg"
                    }
                    alt={
                      serviceOrder.service?.title || serviceOrder.service_name
                    }
                    className="rounded me-3"
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                    }}
                  />
                )}
                <div>
                  <h5 className="mb-2">
                    {serviceOrder.service?.title ||
                      serviceOrder.service_name ||
                      "Service"}
                  </h5>
                  <p className="text-muted mb-2">
                    {serviceOrder.service?.description ||
                      serviceOrder.service?.details?.description}
                  </p>
                  <div className="d-flex gap-2 align-items-center">
                    {getStatusBadge(serviceOrder.status)}
                    {(serviceOrder.service?.category ||
                      serviceOrder.service?.details?.category) && (
                      <Badge color="light" className="text-dark">
                        {serviceOrder.service?.category?.name ||
                          serviceOrder.service?.details?.category?.name}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <div className="service-order-price">
                <h4 className="text-success mb-0">
                  {formatPrice(serviceOrder.total_amount || serviceOrder.total)}
                </h4>
                <small className="text-muted">Total Amount</small>
              </div>
            </Col>
          </Row>

          {/* Service Order Details */}
          <Row className="mb-4">
            <Col md={6}>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-information-line me-2"></i>
                  Order Information
                </h6>
                <div className="info-item mb-2">
                  <strong>Order ID:</strong> #
                  {serviceOrder.order_number || serviceOrder.id}
                </div>
                <div className="info-item mb-2">
                  <strong>Service Type:</strong>{" "}
                  {serviceOrder.service?.service_type || "Standard"}
                </div>
                <div className="info-item mb-2">
                  <strong>Delivery Time:</strong>{" "}
                  {serviceOrder.service?.delivery_time || "Not specified"}
                </div>
                {serviceOrder.delivery_date && (
                  <div className="info-item mb-2">
                    <strong>Expected Delivery:</strong>{" "}
                    {formatDate(serviceOrder.delivery_date)}
                  </div>
                )}
                <div className="info-item mb-2">
                  <strong>Created:</strong>{" "}
                  {formatDateTime(serviceOrder.created_at)}
                </div>
                <div className="info-item mb-2">
                  <strong>Last Updated:</strong>{" "}
                  {formatDateTime(serviceOrder.updated_at)}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-user-line me-2"></i>
                  Customer Information
                </h6>
                <div className="info-item mb-2">
                  <strong>Customer Name:</strong>{" "}
                  {serviceOrder.user?.name || "N/A"}
                </div>
                <div className="info-item mb-2">
                  <strong>Email:</strong> {serviceOrder.user?.email || "N/A"}
                </div>
                <div className="info-item mb-2">
                  <strong>Phone:</strong> {serviceOrder.user?.phone || "N/A"}
                </div>
                <div className="info-item mb-2">
                  <strong>User Type:</strong>{" "}
                  {serviceOrder.user?.account_type_id === 1
                    ? "Influencer"
                    : serviceOrder.user?.account_type_id === 2
                    ? "Business"
                    : "User"}
                </div>
              </div>
            </Col>
          </Row>

          {/* Service Provider Information */}
          <Row className="mb-4">
            <Col md={6}>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-service-line me-2"></i>
                  Service Provider
                </h6>
                <div className="info-item mb-2">
                  <strong>Provider Name:</strong>{" "}
                  {serviceOrder.service_provider?.name || "Unknown Provider"}
                </div>
                <div className="info-item mb-2">
                  <strong>Provider Email:</strong>{" "}
                  {serviceOrder.service_provider?.email || "N/A"}
                </div>
                <div className="info-item mb-2">
                  <strong>Provider Phone:</strong>{" "}
                  {serviceOrder.service_provider?.phone || "N/A"}
                </div>
                <div className="info-item mb-2">
                  <strong>Provider Type:</strong>{" "}
                  {serviceOrder.service_provider?.account_type_id === 1
                    ? "Influencer"
                    : serviceOrder.service_provider?.account_type_id === 2
                    ? "Business"
                    : "User"}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-money-dollar-circle-line me-2"></i>
                  Pricing Details
                </h6>
                <div className="info-item mb-2">
                  <strong>Service Price:</strong>{" "}
                  {formatPrice(
                    serviceOrder.service_price ||
                      serviceOrder.service?.price ||
                      serviceOrder.total_amount ||
                      serviceOrder.total
                  )}
                </div>
                {serviceOrder.additional_fees && (
                  <div className="info-item mb-2">
                    <strong>Additional Fees:</strong>{" "}
                    {formatPrice(serviceOrder.additional_fees)}
                  </div>
                )}
                <div className="info-item mb-2">
                  <strong>Total Amount:</strong>{" "}
                  <span className="text-success fw-bold">
                    {formatPrice(
                      serviceOrder.total_amount || serviceOrder.total
                    )}
                  </span>
                </div>
              </div>
            </Col>
          </Row>

          {/* Contact Details */}
          {serviceOrder.contact_details && (
            <Row className="mb-4">
              <Col>
                <div className="info-section">
                  <h6 className="text-primary mb-3">
                    <i className="ri-contacts-line me-2"></i>
                    Contact Details
                  </h6>
                  <div className="contact-details-content p-3 bg-light rounded">
                    {typeof serviceOrder.contact_details === "string" ? (
                      <pre className="mb-0">{serviceOrder.contact_details}</pre>
                    ) : (
                      <div>
                        {serviceOrder.contact_details.firstName && (
                          <p className="mb-1">
                            <strong>Name:</strong>{" "}
                            {serviceOrder.contact_details.firstName}{" "}
                            {serviceOrder.contact_details.lastName}
                          </p>
                        )}
                        {serviceOrder.contact_details.email && (
                          <p className="mb-1">
                            <strong>Email:</strong>{" "}
                            {serviceOrder.contact_details.email}
                          </p>
                        )}
                        {serviceOrder.contact_details.phone && (
                          <p className="mb-1">
                            <strong>Phone:</strong>{" "}
                            {serviceOrder.contact_details.phone}
                          </p>
                        )}
                        {serviceOrder.contact_details.address && (
                          <p className="mb-1">
                            <strong>Address:</strong>{" "}
                            {serviceOrder.contact_details.address}
                          </p>
                        )}
                        {serviceOrder.contact_details.notes && (
                          <p className="mb-0">
                            <strong>Notes:</strong>{" "}
                            {serviceOrder.contact_details.notes}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Customer Requirements */}
          {serviceOrder.requirements && (
            <Row className="mb-4">
              <Col>
                <div className="info-section">
                  <h6 className="text-primary mb-3">
                    <i className="ri-file-list-line me-2"></i>
                    Customer Requirements
                  </h6>
                  <div className="requirements-content p-3 bg-light rounded">
                    <p className="mb-0">{serviceOrder.requirements}</p>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Additional Notes */}
          {serviceOrder.notes && (
            <Row className="mb-4">
              <Col>
                <div className="info-section">
                  <h6 className="text-primary mb-3">
                    <i className="ri-sticky-note-line me-2"></i>
                    Additional Notes
                  </h6>
                  <div className="notes-content p-3 bg-light rounded">
                    <p className="mb-0">{serviceOrder.notes}</p>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Service Order Timeline */}
          <Row>
            <Col>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-time-line me-2"></i>
                  Order Timeline
                </h6>
                <div className="timeline">
                  <div className="timeline-item d-flex justify-content-between align-items-center p-2 border-bottom">
                    <span>Service Order Created</span>
                    <small className="text-muted">
                      {formatDateTime(serviceOrder.created_at)}
                    </small>
                  </div>
                  {serviceOrder.confirmed_at && (
                    <div className="timeline-item d-flex justify-content-between align-items-center p-2 border-bottom">
                      <span>Order Confirmed</span>
                      <small className="text-muted">
                        {formatDateTime(serviceOrder.confirmed_at)}
                      </small>
                    </div>
                  )}
                  {serviceOrder.started_at && (
                    <div className="timeline-item d-flex justify-content-between align-items-center p-2 border-bottom">
                      <span>Work Started</span>
                      <small className="text-muted">
                        {formatDateTime(serviceOrder.started_at)}
                      </small>
                    </div>
                  )}
                  {serviceOrder.completed_at && (
                    <div className="timeline-item d-flex justify-content-between align-items-center p-2 border-bottom">
                      <span>Order Completed</span>
                      <small className="text-muted">
                        {formatDateTime(serviceOrder.completed_at)}
                      </small>
                    </div>
                  )}
                  {serviceOrder.updated_at !== serviceOrder.created_at && (
                    <div className="timeline-item d-flex justify-content-between align-items-center p-2 border-bottom">
                      <span>Last Updated</span>
                      <small className="text-muted">
                        {formatDateTime(serviceOrder.updated_at)}
                      </small>
                    </div>
                  )}
                  {serviceOrder.delivery_date && (
                    <div className="timeline-item d-flex justify-content-between align-items-center p-2">
                      <span>Expected Delivery</span>
                      <small className="text-muted">
                        {formatDateTime(serviceOrder.delivery_date)}
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </ModalBody>

      <ModalFooter>
        <div className="d-flex justify-content-between w-100">
          <div className="status-actions">
            <h6 className="mb-2">Update Status:</h6>
            <div className="d-flex gap-2 flex-wrap">
              {serviceOrder.status !== "pending" && (
                <Button
                  color="warning"
                  size="sm"
                  onClick={() => updateServiceOrderStatus("pending")}
                  disabled={isUpdating}
                >
                  <i className="ri-time-line me-1"></i>
                  Pending
                </Button>
              )}
              {serviceOrder.status !== "accepted" && (
                <Button
                  color="info"
                  size="sm"
                  onClick={() => updateServiceOrderStatus("accepted")}
                  disabled={isUpdating}
                >
                  <i className="ri-check-line me-1"></i>
                  Accept
                </Button>
              )}
              {serviceOrder.status !== "in_progress" && (
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => updateServiceOrderStatus("in_progress")}
                  disabled={isUpdating}
                >
                  <i className="ri-play-line me-1"></i>
                  In Progress
                </Button>
              )}
              {serviceOrder.status !== "completed" && (
                <Button
                  color="success"
                  size="sm"
                  onClick={() => updateServiceOrderStatus("completed")}
                  disabled={isUpdating}
                >
                  <i className="ri-check-double-line me-1"></i>
                  Complete
                </Button>
              )}
              {serviceOrder.status !== "cancelled" && (
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => updateServiceOrderStatus("cancelled")}
                  disabled={isUpdating}
                >
                  <i className="ri-close-line me-1"></i>
                  Cancel
                </Button>
              )}
            </div>
          </div>
          <div>
            <Button color="secondary" onClick={tog_center}>
              Close
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
}
